import { supabase } from '@/lib/supabase'
import { captureError, recordBreadcrumb } from '@/lib/sentry'

/**
 * `join_group_via_invite` RPC'sinin iş kuralı redleri (RAISE EXCEPTION metinleri).
 * Bunlar arıza değil, kullanıcının karşılaşabileceği beklenen durumlar (GUNUM-VAR-1T).
 */
const EXPECTED_JOIN_ERRORS: Record<string, string> = {
  'Group is full': 'Bu grup dolu, yeni üye alamıyor.',
  'Invalid or expired invite code': 'Bu davet bağlantısı geçersiz ya da süresi dolmuş.',
}

/** Kullanıcıya gösterilecek Türkçe mesajı taşıyan beklenen katılım reddi. */
export class InviteJoinError extends Error {
  readonly expected = true
}

export function isExpectedInviteError(error: unknown): error is InviteJoinError {
  return error instanceof InviteJoinError
}

function toExpectedJoinError(error: unknown): InviteJoinError | null {
  const message = (error as { message?: unknown } | null)?.message
  if (error instanceof Error || typeof message !== 'string') return null
  const turkish = EXPECTED_JOIN_ERRORS[message]
  return turkish ? new InviteJoinError(turkish) : null
}

export interface GroupPreview {
  group_name: string
  asset_type: string
  currency: string
  amount: number
  cycle_period: string
  inviter_name: string
  inviter_avatar: string | null
  member_count: number
  max_members?: number | null
  invite_deadline_at?: string | null
  lottery_due_at?: string | null
  lottery_type?: string
  /** Göç sonrası: koltuğa bağlı davette koltuk ve takvim bilgisi */
  seat?: { display_name: string; turn_index: number }
  start_date?: string | null
  masked_members: {
    initials: string
    avatar_url: string | null
    turn_index: number
  }[]
}

type PreviewRow = GroupPreview & {
  seat_display_name?: string | null
  seat_turn_index?: number | null
}

/**
 * Kişiye özel koltuk daveti: RPC'nin düz seat_* sütunlarını InviteView'un
 * beklediği `seat` nesnesine çevirir. Koltuksuz davette (ya da göç öncesi)
 * önizleme olduğu gibi döner.
 */
function withSeat(row: PreviewRow): GroupPreview {
  if (!('seat_display_name' in row) && !('seat_turn_index' in row)) return row
  const { seat_display_name, seat_turn_index, ...rest } = row
  if (!seat_display_name || !seat_turn_index) return rest
  return { ...rest, seat: { display_name: seat_display_name, turn_index: seat_turn_index } }
}

export const inviteService = {
  /**
   * Invite kodu ile grup önizleme bilgilerini getirir.
   * Login gerektirmez.
   */
  async getInvitePreview(code: string): Promise<GroupPreview | null> {
    try {
      const { data, error } = await supabase.rpc('get_group_preview_by_invite', {
        code_input: code,
      })

      if (error) throw error

      // RPC returns a table/list, but we expect single record logic inside logic
      // But since RPC returns table, data is an array.
      if (!data || !Array.isArray(data) || data.length === 0) return null

      return withSeat(data[0] as unknown as PreviewRow)
    } catch (error) {
      captureError(error, { service: 'inviteService', action: 'getInvitePreview', code })
      return null
    }
  },

  /**
   * Davetiye önizlemesini getirir ve kullanıcının ücretsiz grup sınırına
   * ulaşıp ulaşmadığını eşzamanlı sorgular. (Premium upsell için)
   */
  async getInviteAccessDetails(code: string): Promise<{ preview: GroupPreview | null, isLimitReached: boolean }> {
    try {
      // 1. Fetch preview, session, and invite's group_id concurrently
      const [previewResponse, sessionResponse, inviteResponse] = await Promise.all([
        supabase.rpc('get_group_preview_by_invite', { code_input: code }),
        supabase.auth.getSession(),
        supabase.from('group_invitations' as any).select('group_id').eq('invite_code', code).maybeSingle()
      ])

      const previewData = previewResponse.data
      if (previewResponse.error || !previewData || !Array.isArray(previewData) || previewData.length === 0) {
        return { preview: null, isLimitReached: false }
      }
      
      const preview = withSeat(previewData[0] as unknown as PreviewRow)
      const session = sessionResponse.data.session

      if (!session) return { preview, isLimitReached: false }

      const inviteData = inviteResponse.data as { group_id: string } | null

      if (inviteData && inviteData.group_id) {
        // 2. Concurrently check if user is ALREADY an active member AND check their premium limit
        const { groupService } = await import('@/services/groupService')
        
        const [existingMemberResponse, canJoin] = await Promise.all([
          supabase
            .from('group_members')
            .select('status')
            .eq('group_id', inviteData.group_id)
            .eq('user_id', session.user.id)
            .maybeSingle(),
          groupService.canCreateGroup()
        ])

        const existingMember = existingMemberResponse.data

        if (!existingMember || existingMember.status !== 'active') {
          // Not an active member -> return the limit status
          return { preview, isLimitReached: !canJoin }
        }
      }
      
      return { preview, isLimitReached: false }
    } catch (error) {
      captureError(error, { service: 'inviteService', action: 'getInviteAccessDetails', code })
      return { preview: null, isLimitReached: false }
    }
  },

  /**
   * Daveti kabul ederek gruba katılır.
   * Kullanıcı login olmuş olmalıdır.
   */
  async acceptInvite(code: string) {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Kullanıcı oturumu yok')

      // Grupa zaten dahil mi? Değilse, Premium limitini kontrol et.
      const response = await supabase
        .from('group_invitations' as any)
        .select('group_id')
        .eq('invite_code', code)
        .maybeSingle()

      const inviteData = response.data as { group_id: string } | null;

      if (inviteData && inviteData.group_id) {
        const { data: existingMember } = await supabase
          .from('group_members')
          .select('status')
          .eq('group_id', inviteData.group_id)
          .eq('user_id', user.id)
          .maybeSingle()

        if (!existingMember || existingMember.status !== 'active') {
          // Henüz aktif üye değil, dolayısıyla gruba dahil olma işlemi yapacak. Limitini kontrol edelim.
          const { groupService } = await import('@/services/groupService')
          const canJoin = await groupService.canCreateGroup()
          if (!canJoin) {
            throw new Error("Sosyal ağınız giderek büyüyor! Çalışma arkadaşlarınız, dostlarınız ve tüm sevdiklerinizle sınır olmadan sosyalleşmeye devam etmek için Premium'a geçin.")
          }
        }
      }

      const { data, error } = await supabase.rpc('join_group_via_invite', {
        code_input: code,
      })

      if (error) {
        const expected = toExpectedJoinError(error)
        if (expected) {
          recordBreadcrumb('Davet katılımı iş kuralıyla reddedildi', {
            service: 'inviteService',
            reason: error.message,
            code,
          })
          throw expected
        }
        throw error
      }
      return data
    } catch (error) {
      if (isExpectedInviteError(error)) throw error
      captureError(error, { service: 'inviteService', action: 'acceptInvite', code })
      throw error
    }
  },


}
